import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FacilityCard from '../components/FacilityCard';
import SearchFilters from '../components/SearchFilters';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { facilityApi, handleApiError } from '../utils/api';
import { mockStates } from '../data/mock'; // Keep for state info

const SearchPage = () => {
  const location = useLocation();
  // Lazy-init from the URL so the very first fetch already has the right
  // filters instead of firing an unfiltered request that a later, filtered
  // request then has to race against.
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(location.search).get('q') || ''
  );
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => ({
    state: new URLSearchParams(location.search).get('state') || '',
    treatmentTypes: [],
    specializations: [],
    insurance: []
  }));

  // State for API data
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);

  // Reset to page 1 whenever the search term or filters change, so a stale
  // page number from a previous, narrower result set can't leave the user
  // stranded on an out-of-range page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  // Fetch facilities based on current search criteria
  useEffect(() => {
    let cancelled = false;

    const fetchFacilities = async () => {
      setLoading(true);
      setError(null);

      try {
        // Build search parameters (backend uses skip/limit, not page)
        const searchParams = {
          skip: (currentPage - 1) * 20,
          limit: 20
        };

        // Add search query
        if (searchQuery.trim()) {
          searchParams.q = searchQuery.trim();
        }

        // Add filters
        if (filters.state) {
          searchParams.state = filters.state;
        }
        if (filters.treatmentTypes.length > 0) {
          searchParams.treatment_types = filters.treatmentTypes.join(',');
        }
        if (filters.specializations.length > 0) {
          searchParams.specializations = filters.specializations.join(',');
        }
        if (filters.insurance.length > 0) {
          searchParams.insurance = filters.insurance.join(',');
        }

        // Use search endpoint for comprehensive results
        const response = await facilityApi.search(searchParams);
        if (cancelled) return;

        setFacilities(response.facilities || []);
        setTotalCount(response.total || response.facilities?.length || 0);

      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching facilities:', err);
        setError(err.message);
        setFacilities([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFacilities();
    return () => { cancelled = true; };
  }, [searchQuery, filters, currentPage, sortBy]);

  // Apply client-side sorting to facilities (since API might not support all sort options)
  const sortedFacilities = useMemo(() => {
    const sorted = [...facilities];
    
    switch (sortBy) {
      case 'rating':
        sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'state':
        sorted.sort((a, b) => (a.stateFullName || a.state || '').localeCompare(b.stateFullName || b.state || ''));
        break;
      default:
        // Relevance - keep API order
        break;
    }
    
    return sorted;
  }, [facilities, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL without page reload
    const params = new URLSearchParams(location.search);
    if (searchQuery.trim()) {
      params.set('q', searchQuery);
    } else {
      params.delete('q');
    }
    window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
  };

  const clearAllFilters = () => {
    setFilters({
      state: '',
      treatmentTypes: [],
      specializations: [],
      insurance: []
    });
    setSearchQuery('');
    setCurrentPage(1);
    window.history.replaceState({}, '', location.pathname);
  };

  const getActiveFiltersCount = () => {
    return [
      filters.state ? 1 : 0,
      filters.treatmentTypes.length,
      filters.specializations.length,
      filters.insurance.length
    ].reduce((a, b) => a + b, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 max-w-2xl">
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Search facilities, cities, or treatment types..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <Button type="submit">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Relevance</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="state">State</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filters
                {getActiveFiltersCount() > 0 && (
                  <Badge className="ml-2">{getActiveFiltersCount()}</Badge>
                )}
              </Button>
            </div>
          </div>
          
          {/* Active Filters Display */}
          {(searchQuery || getActiveFiltersCount() > 0) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              
              {searchQuery && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Search: "{searchQuery}"
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => setSearchQuery('')}
                  />
                </Badge>
              )}
              
              {filters.state && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  State: {mockStates.find(s => s.code === filters.state)?.name}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => setFilters(prev => ({ ...prev, state: '' }))}
                  />
                </Badge>
              )}
              
              {filters.treatmentTypes.map((type) => (
                <Badge key={type} variant="secondary" className="flex items-center gap-1">
                  {type}
                  <X 
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      treatmentTypes: prev.treatmentTypes.filter(t => t !== type)
                    }))}
                  />
                </Badge>
              ))}
              
              {getActiveFiltersCount() > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear All
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Filters - Desktop */}
          <div className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <SearchFilters
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={clearAllFilters}
            />
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Results Summary */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Addiction Treatment Centers
                {searchQuery && ` for "${searchQuery}"`}
                {filters.state && ` in ${mockStates.find(s => s.code === filters.state)?.name}`}
              </h1>
              <p className="text-gray-600">
                {loading ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Searching treatment facilities...
                  </span>
                ) : (
                  <>
                    Found {totalCount} treatment facilities
                    {searchQuery || getActiveFiltersCount() > 0 ? ' matching your criteria' : ''}
                  </>
                )}
              </p>
            </div>

            {/* Facilities Grid */}
            {loading ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Searching Facilities</h3>
                  <p className="text-gray-600">Please wait while we find treatment centers for you...</p>
                </CardContent>
              </Card>
            ) : error ? (
              <Card>
                <CardContent className="text-center py-12">
                  <div className="text-red-500 mb-4">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">Search Error</h3>
                    <p>There was an error searching for facilities: {error}</p>
                  </div>
                  <Button onClick={() => window.location.reload()} variant="outline">
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            ) : sortedFacilities.length > 0 ? (
              <>
                <div className="grid md:grid-cols-2 gap-6">
                  {sortedFacilities.map((facility) => (
                    <FacilityCard 
                      key={facility.id} 
                      facility={facility} 
                      showState={!filters.state}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalCount > 20 && (
                  <div className="mt-12 flex justify-center">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="px-4 py-2">
                        Page {currentPage} of {Math.ceil(totalCount / 20)}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= Math.ceil(totalCount / 20)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <div className="text-gray-500 mb-4">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">No facilities found</h3>
                    <p>
                      Try adjusting your search criteria or browse all facilities
                    </p>
                  </div>
                  <Button onClick={clearAllFilters} variant="outline">
                    Clear Filters & View All
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;